import endpoints from "./endpoints.js";
import * as localDb from "../localDb/index.js";
import postApiData from "./postApiData.js";
import moment from "moment/moment.js";

// Reverse alias map: long-form entity names (mdc-expo canonical) → short-form
// (ruuts-mobile-data canonical). Pictures created in mdc-expo arrive with the
// long form; ruuts-mobile-data stores and queries them in the short form.
// Mirrors mdc-expo's normalizePictureEntity in the opposite direction.
const pictureEntityAliasMap = {
    monitoringTask: "task",
    monitoringSite: "site",
    monitoringEvent: "event",
    monitoringActivity: "activity",
};

function normalizePictureEntity(picture) {
    const mapped = pictureEntityAliasMap[picture?.entity];
    if (mapped) {
        return { ...picture, entity: mapped };
    }
    return picture;
}

const EVENT_BATCH_SIZE = 20;

export default async function downloadMonitoringData(user, onProgress) {
    try {
        let db = await localDb.getDb();
        const localEventIds = await db.events.toCollection().primaryKeys();
        const localSitesIds = await db.sites.toCollection().primaryKeys();
        const localSitesIdSet = new Set(localSitesIds);

        const baseBody = {
            assignedTo: user["/hubId"] === "all" || user["/hubId"].includes("all") ? null : user.name,
            localEvents: localEventIds,
        };

        // === Step 1: Structure data (farms, sampling areas, paddocks, sites) ===
        onProgress?.({ step: "structure", progress: 0.1 });

        const { payload: structData } = await postApiData(endpoints.syncDownloadNew, {
            ...baseBody,
            structureOnly: true,
        });

        await db.transaction("rw", ["farms", "samplingAreas", "sites", "paddocks"], async () => {
            await Promise.all([
                db.farms.bulkPut(structData.farms),
                db.samplingAreas.bulkPut(structData.samplingAreas),
                db.paddocks.bulkPut(structData.paddocks),
                db.sites.bulkPut(
                    structData.monitoringSites.filter((site) => !localSitesIdSet.has(site.id)),
                ),
            ]);
        });

        // Handle deleted sites from structure batch
        if (structData.deletedMonitoringSiteIds && structData.deletedMonitoringSiteIds.length) {
            await handleDeletedSites(db, structData.deletedMonitoringSiteIds);
        }

        // === Step 2: Events in atomic batches ===
        let offset = 0;
        let totalEvents = null;

        while (true) {
            const progress =
                totalEvents != null && totalEvents > 0
                    ? 0.2 + 0.7 * Math.min(offset / totalEvents, 1)
                    : 0.2;
            onProgress?.({ step: "events", progress, offset, total: totalEvents });

            const { payload: batchData } = await postApiData(endpoints.syncDownloadNew, {
                ...baseBody,
                eventLimit: EVENT_BATCH_SIZE,
                eventOffset: offset,
            });

            if (totalEvents === null) {
                totalEvents = batchData.newEventsTotal || 0;
            }

            // Handle deleted events (only on first batch)
            if (offset === 0 && batchData.deletedEvents && batchData.deletedEvents.length) {
                let deletedEventsToUpdate = batchData.deletedEvents.map((deletedEvent) => ({
                    key: deletedEvent,
                    changes: {
                        isDeleted: true,
                        updatedAt: moment.utc().format(),
                    },
                }));
                await db.events.bulkUpdate(deletedEventsToUpdate);
            }

            // If no new events in this batch, we're done
            if (!batchData.newEvents || !batchData.newEvents.length) break;

            // Write batch atomically (event + activities + tasks + pictures together)
            await db.transaction("rw", ["events", "activities", "tasks", "pictures"], async () => {
                await Promise.all([
                    db.events.bulkPut(batchData.newEvents),
                    db.activities.bulkPut(batchData.monitoringActivity || []),
                    db.tasks.bulkPut(batchData.monitoringTasks || []),
                    db.pictures.bulkPut((batchData.monitoringPictures || []).map(normalizePictureEntity)),
                ]);
            });

            offset += batchData.newEvents.length;

            // If we got fewer than the limit, no more events to fetch
            if (batchData.newEvents.length < EVENT_BATCH_SIZE) break;
        }

        onProgress?.({ step: "complete", progress: 1.0 });
        return "Success";
    } catch (error) {
        console.error(error);
        throw error;
    }
}

async function handleDeletedSites(db, deletedMonitoringSiteIds) {
    await db.transaction("rw", ["changelog", "sites", "activities", "tasks", "pictures"], async (db) => {
        const monitoringSiteIds = deletedMonitoringSiteIds;
        const tasksToDelete = db.tasks
            .where("monitoringSiteId")
            .anyOf(monitoringSiteIds)
            .and((task) => !task.isDeleted);
        const sitePicturesToDelete = db.pictures
            .where("entityId")
            .anyOf(monitoringSiteIds)
            .and((picture) => !picture.isDeleted);
        const activitiesToDelete = db.activities
            .where("monitoringSiteId")
            .anyOf(monitoringSiteIds)
            .and((activity) => !activity.isDeleted);
        const sitesToDelete = db.sites
            .where("id")
            .anyOf(monitoringSiteIds)
            .and((site) => !site.isDeleted);

        const tasksToDeleteArray = await tasksToDelete.toArray();
        const taskIds = tasksToDeleteArray.map((task) => task.id);
        if (taskIds.length) {
            await db.pictures
                .where("entityId")
                .anyOf(taskIds)
                .and((picture) => picture.entity === "task" && !picture.isDeleted)
                .modify({ isDeleted: true });
        }

        await Promise.all([
            tasksToDelete.modify({ isDeleted: true }),
            sitePicturesToDelete.modify({ isDeleted: true }),
            activitiesToDelete.modify({ isDeleted: true }),
            sitesToDelete.modify({ isDeleted: true }),
        ]);

        const sitePicturesToDeleteArray = await sitePicturesToDelete.toArray();
        const activitiesToDeleteArray = await activitiesToDelete.toArray();
        const sitesToDeleteArray = await sitesToDelete.toArray();

        await Promise.all([
            removeChangelogEntries(db, "tasks", taskIds),
            removeChangelogEntries(
                db,
                "pictures",
                sitePicturesToDeleteArray.map((picture) => picture.id),
            ),
            removeChangelogEntries(
                db,
                "activities",
                activitiesToDeleteArray.map((activity) => activity.id),
            ),
            removeChangelogEntries(
                db,
                "sites",
                sitesToDeleteArray.map((site) => site.id),
            ),
        ]);
    });
}

async function removeChangelogEntries(db, entity, ids) {
    if (!ids || !ids.length) {
        return;
    }
    await db.changelog
        .where("entityId")
        .anyOf(ids)
        .and((entry) => entry.syncStatus === "not-synced" && entry.entity === entity)
        .delete();
}
